import {
	ArcElement,
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Filler,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from "chart.js";
import { formatMoney } from "@pages/profile-v2/utils";

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	ArcElement,
	Filler,
	Tooltip,
	Legend
);

export const CHART_COLORS = {
	primary: "rgba(99, 179, 237, 0.95)",
	primaryFill: "rgba(99, 179, 237, 0.2)",
	secondary: "rgba(76, 175, 134, 0.9)",
	secondaryFill: "rgba(76, 175, 134, 0.15)",
	tertiary: "rgba(255, 183, 77, 0.9)",
	tertiaryFill: "rgba(255, 183, 77, 0.15)",
	danger: "rgba(239, 83, 80, 0.9)",
	dangerFill: "rgba(239, 83, 80, 0.15)",
	purple: "rgba(171, 130, 255, 0.9)",
	grid: "rgba(110, 113, 145, 0.2)",
	text: "rgba(170, 170, 187, 0.95)",
	tooltipBg: "rgba(22, 24, 35, 0.96)",
};

export const MIN_SLICE_RATIO = 0.08;

export const formatDateLabel = (date) => {
	if (!date) {
		return "";
	}

	const parts = date.split("-");
	if (parts.length === 3) {
		return `${parts[2]}/${parts[1]}`;
	}

	return date;
};

export const hasActivity = (values) =>
	values.some((value) => Math.abs(Number(value) || 0) > 0.001);

export const getVisualSliceValue = (value, total) => {
	if (!value) {
		return 0;
	}

	const naturalRatio = value / total;

	if (naturalRatio >= MIN_SLICE_RATIO) {
		return value;
	}

	return total * MIN_SLICE_RATIO;
};

export const createGradient = (ctx, chartArea, colorStops) => {
	if (!chartArea) {
		return colorStops[colorStops.length - 1]?.color || CHART_COLORS.primary;
	}

	const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
	colorStops.forEach(({ offset, color }) => gradient.addColorStop(offset, color));

	return gradient;
};

export const getBaseChartOptions = () => ({
	responsive: true,
	maintainAspectRatio: false,
	interaction: {
		mode: "index",
		intersect: false,
	},
	plugins: {
		legend: {
			position: "top",
			align: "end",
			labels: {
				color: CHART_COLORS.text,
				usePointStyle: true,
				pointStyle: "circle",
				padding: 18,
				font: { size: 11, weight: "600" },
			},
		},
		tooltip: {
			backgroundColor: CHART_COLORS.tooltipBg,
			titleColor: "#FCFCFC",
			bodyColor: CHART_COLORS.text,
			borderColor: "rgba(110, 113, 145, 0.35)",
			borderWidth: 1,
			padding: 12,
			cornerRadius: 10,
			displayColors: true,
			boxPadding: 4,
		},
	},
	scales: {
		x: {
			grid: { color: CHART_COLORS.grid, drawBorder: false },
			ticks: {
				color: CHART_COLORS.text,
				maxRotation: 45,
				minRotation: 0,
				font: { size: 10 },
			},
		},
		y: {
			grid: { color: CHART_COLORS.grid, drawBorder: false },
			ticks: { color: CHART_COLORS.text, font: { size: 10 } },
			beginAtZero: true,
		},
	},
});

export const formatAxisValue = (value, currency) => {
	const num = Number(value);

	if (!num) {
		return "0";
	}

	const abs = Math.abs(num);

	if (abs >= 1000) {
		const compact = num / 1000;
		const formatted =
			compact % 1 === 0 ? compact.toFixed(0) : compact.toFixed(1);

		return `${formatted}k`;
	}

	return formatMoney(num, currency);
};

export const formatDurationTooltip = (minutes) => {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;

	if (hours > 0) {
		return `${hours}h ${mins}m`;
	}

	return `${mins}m`;
};
